/**
 * Struktur-bewusster Renderer fuer Chatbot-Antworten.
 *
 * Der Command-Chatbot liefert Text nach einem festen "Antwort-Vertrag" (siehe
 * docs/design-proposals/chatbot-readability-2026-06-05/): Sektions-Header mit
 * Anzahl (`Titel (N):`), Strich-/Punkt-Listen (`- ` / `• `), Feld-Zeilen
 * (`- Label: Wert`), Rankings (`1. Name: Wert`), Inline-Metadaten (` — dringend`,
 * `(ab 22.06.2026)`, `⚠ …`, `[Abteilung]`) sowie Mehr-Hinweise und Fussnoten.
 *
 * Diese Funktion uebersetzt diesen Vertrag in semantisches, ausschliesslich im
 * Blau-/Neutral-Schema gestaltetes HTML (Klassen `cm-*`, gestylt via ::ng-deep in
 * der MessageBubble-Component). Dringlichkeit wird ueber Fettung, gefuelltes-vs-
 * umrandetes blaues Chip und das ⚠-Symbol vermittelt — bewusst KEINE zweite Farbe.
 *
 * Sicherheit: dynamischer Text wird ge-escaped (`&`,`<`,`>`); HTML entsteht nur aus
 * den vertrauenswuerdigen Templates hier. Inline-Syntax `**fett**`, `[Label](href)`
 * und `` `code` `` bleibt erhalten (Links werden ueber den Bubble-Click-Handler
 * geroutet).
 */

const HTML_ESCAPE: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;' };

function esc(s: string): string {
  return s.replace(/[&<>]/g, (c) => HTML_ESCAPE[c]);
}

function escAttr(s: string): string {
  return s.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/** Inline-Formatierung auf bereits ge-escapetem Text: **fett**, [Label](href), `code`. */
function inline(escaped: string): string {
  let s = escaped;
  s = s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, label, href) => `<a href="${escAttr(href)}">${label}</a>`);
  s = s.replace(/`([^`]+)`/g, '<code>$1</code>');
  return s;
}

const PRIORITY_RE = /\s*[—-]\s*(dringend|hoch)\s*$/i;
// Datum am Zeilenende in Klammern: (ab 22.06.2026) / (18.06.–20.06.2026) / (ab 09.06.) …
const DATE_TAIL_RE = /\s*\(((?:ab\s+)?\d{1,2}\.\d{1,2}\.(?:\d{2,4})?[^)]*)\)\s*$/;
const DEPT_TAIL_RE = /\s*\[([^\]]+)\]\s*$/;
// Name/Meta-Trenner: " - " / " — " / " · " (mit Leerzeichen) — NICHT das Slash in "A / B".
const NAME_DASH_RE = /^(.+?)\s+[—–·-]\s+(.+)$/;
// Fallback-Trenner ": " (Label/Wert): kurzer Kopf vor Doppelpunkt+Leerzeichen. Greift NICHT
// bei Uhrzeiten ("16:00") — die haben Ziffer statt Leerzeichen hinter dem Doppelpunkt.
const NAME_COLON_RE = /^([^:?!•]{1,40}):\s+(.+)$/;

/** Rendert den Inhalt EINER Listen-Zeile (ohne fuehrendes "- "/"• ") in ein <li>. */
function renderItem(raw: string): string {
  let text = raw.trim();
  const trailing: string[] = []; // rechtsbuendige Chips (Datum/Prioritaet)
  const appended: string[] = []; // inline an den Haupttext (Warnung/Abteilung)

  // 1) Prioritaets-Suffix (gefuellt = dringend, umrandet = hoch — Unterschied ueber Fuellung).
  const prio = text.match(PRIORITY_RE);
  if (prio) {
    const kind = prio[1].toLowerCase() === 'dringend' ? 'urgent' : 'high';
    trailing.push(`<span class="cm-chip cm-chip--${kind}">${esc(prio[1].toLowerCase())}</span>`);
    text = text.slice(0, prio.index).trimEnd();
  }

  // 2) Warnung: "⚠ …" — das Glyph traegt die Semantik, kein Farbwechsel.
  const wi = text.indexOf('⚠');
  if (wi >= 0) {
    const wtext = text.slice(wi + 1).trim();
    appended.push(`<span class="cm-warn">⚠ ${inline(esc(wtext))}</span>`);
    text = text.slice(0, wi).replace(/[—–\-,;]\s*$/, '').trimEnd();
  }

  // 3) Datum am Ende in Klammern → neutrales Datums-Chip.
  const date = text.match(DATE_TAIL_RE);
  if (date) {
    trailing.push(`<span class="cm-chip cm-chip--date">${esc(date[1])}</span>`);
    text = text.slice(0, date.index).trimEnd();
  }

  // 4) Abteilungs-Tag am Ende: [Abteilung].
  const dept = text.match(DEPT_TAIL_RE);
  if (dept) {
    appended.push(`<span class="cm-tag">${esc(dept[1])}</span>`);
    text = text.slice(0, dept.index).trimEnd();
  }

  // 5) Name/Meta-Aufteilung: fuehrender Kopf (Name oder Feld-Label) wird fett, Rest gedaempft.
  //    Trenner-Vorrang: " - "/" — "/" · "  vor  ": ". So bleibt der Name/das Label IMMER betont
  //    (Personen-Liste, Profil-Feld und Besetzungs-Zeile sehen einheitlich gut aus).
  const split = text.match(NAME_DASH_RE) || text.match(NAME_COLON_RE);
  const mainHtml = split
    ? `<span class="cm-name">${inline(esc(split[1].trim()))}</span>` +
      `<span class="cm-meta"> · ${inline(esc(split[2].trim()))}</span>`
    : `<span class="cm-name">${inline(esc(text))}</span>`;
  return liRow(mainHtml, appended, trailing);
}

function liRow(mainHtml: string, appended: string[], trailing: string[]): string {
  const main = `<span class="cm-main">${mainHtml}${appended.map((a) => ' ' + a).join('')}</span>`;
  return `<li><span class="cm-dot"></span>${main}${trailing.join('')}</li>`;
}

/** Ranking-Zeile "N. Name: Wert" → Rang-Badge + Name + rechtsbuendiger Wert. */
function renderRank(no: string, body: string): string {
  const split = body.match(/^(.+?):\s+(.+)$/);
  if (split) {
    return (
      `<li><span class="cm-rank-no">${esc(no)}</span>` +
      `<span class="cm-rank-name">${inline(esc(split[1].trim()))}</span>` +
      `<span class="cm-rank-val">${inline(esc(split[2].trim()))}</span></li>`
    );
  }
  return `<li><span class="cm-rank-no">${esc(no)}</span><span class="cm-rank-name">${inline(esc(body))}</span></li>`;
}

type Mode = 'none' | 'rows' | 'rank';

export function renderChatMarkdown(md: string): string {
  if (!md) return '';
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const out: string[] = [];
  let mode: Mode = 'none';
  let headerSeen = false;

  const closeList = () => {
    if (mode === 'rows') out.push('</ul>');
    else if (mode === 'rank') out.push('</ol>');
    mode = 'none';
  };
  const openRows = () => {
    if (mode !== 'rows') {
      closeList();
      out.push('<ul class="cm-rows">');
      mode = 'rows';
    }
  };
  const openRank = () => {
    if (mode !== 'rank') {
      closeList();
      out.push('<ol class="cm-rank">');
      mode = 'rank';
    }
  };

  for (const rawLine of lines) {
    const line = rawLine.replace(/\s+$/, '');
    const trimmed = line.trim();

    if (!trimmed) {
      closeList();
      continue;
    }

    // Listen-Zeile: "- ", "• " oder Markdown-"* " (sonst blieb der Stern sichtbar)
    const bullet = trimmed.match(/^[-•*]\s+(.+)$/);
    if (bullet) {
      openRows();
      out.push(renderItem(bullet[1]));
      continue;
    }

    // Ranking-Zeile: "N. …"
    const rank = trimmed.match(/^(\d+)\.\s+(.+)$/);
    if (rank) {
      openRank();
      out.push(renderRank(rank[1], rank[2]));
      continue;
    }

    closeList();

    // Mehr-Hinweis: "… und N weitere." / "(+N weitere)"
    if (/^…?\s*und\s+\d+\s+weitere/i.test(trimmed) || /\(\+\d+\s+weitere\)/i.test(trimmed)) {
      out.push(`<div class="cm-more">${inline(esc(trimmed.replace(/^…\s*/, '… ')))}</div>`);
      continue;
    }

    // Fussnote: komplette Zeile in Klammern, z. B. "(Team gesamt: 65 Tage übrig)"
    if (/^\(.+\)$/.test(trimmed)) {
      out.push(`<div class="cm-foot">${inline(esc(trimmed.replace(/^\(|\)$/g, '')))}</div>`);
      continue;
    }

    // Sektions-Header mit Anzahl: "Titel (5):" / "Titel (5, davon 2 dringend):"
    const headCount = trimmed.match(/^(.+?)\s*\((\d[^)]*)\):$/);
    if (headCount) {
      const countMatch = headCount[2].match(/^(\d+)(.*)$/);
      const num = countMatch ? countMatch[1] : headCount[2];
      const note = countMatch && countMatch[2].trim() ? countMatch[2].trim().replace(/^,\s*/, '') : '';
      const top = headerSeen ? '' : ' cm-head--top';
      headerSeen = true;
      out.push(
        `<div class="cm-head${top}"><span class="cm-htext">${inline(esc(headCount[1].trim()))}</span>` +
          `<span class="cm-count">${esc(num)}</span>` +
          (note ? `<span class="cm-note">${inline(esc(note))}</span>` : '') +
          `</div>`,
      );
      continue;
    }

    // Block-/Unter-Header ohne Anzahl: "Titel:" (kein Doppelpunkt-Satz mitten im Text)
    const head = trimmed.match(/^([^:]{1,48}):$/);
    if (head) {
      headerSeen = true;
      out.push(`<div class="cm-subhead">${inline(esc(head[1].trim()))}</div>`);
      continue;
    }

    // sonst: Absatz
    out.push(`<p>${inline(esc(trimmed))}</p>`);
  }

  closeList();
  return out.join('');
}
